import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  alignVariants,
  type HeadingElement,
  toneVariants,
  truncateVariants,
} from "./shared";

const headlineVariants = cva("", {
  variants: {
    size: {
      lg: [
        "font-(family-name:--font-family-headline)",
        "text-(length:--font-size-headline-lg)",
        "leading-(--line-height-40)",
        "font-medium",
        "tracking-normal",
      ],
      md: [
        "font-(family-name:--font-family-headline)",
        "text-(length:--font-size-headline-md)",
        "leading-(--line-height-36)",
        "font-medium",
        "tracking-normal",
      ],
      sm: [
        "font-(family-name:--font-family-headline)",
        "text-(length:--font-size-headline-sm)",
        "leading-(--line-height-32)",
        "font-medium",
        "tracking-normal",
      ],
    },
    tone: toneVariants,
    align: alignVariants,
    truncate: truncateVariants,
  },
  defaultVariants: {
    size: "md",
    tone: "default",
  },
});

type HeadlineSize = NonNullable<VariantProps<typeof headlineVariants>["size"]>;

const defaultElementMap: Record<HeadlineSize, HeadingElement> = {
  lg: "h2",
  md: "h3",
  sm: "h4",
};

export interface HeadlineProps
  extends Omit<React.HTMLAttributes<HTMLHeadingElement>, "className">,
    VariantProps<typeof headlineVariants> {
  /** Override the default HTML element */
  as?: HeadingElement;
}

/**
 * Headline component for section headers and page titles.
 * Uses Montserrat font family with medium weight.
 *
 * Sizes: large (32px), medium (28px), small (24px)
 */
function Headline({
  size = "md",
  tone,
  align,
  truncate,
  as,
  ...props
}: HeadlineProps) {
  const defaultElement = size ? defaultElementMap[size] : "h3";
  const Element = as || defaultElement;

  return React.createElement(Element, {
    className: headlineVariants({ size, tone, align, truncate }),
    ...props,
  });
}

export { Headline };
